import React from "react";
import intl from "react-intl-universal";
import {computed, makeObservable} from "mobx";

import {ColId, ColSchema} from "../../ui/components/Table";
import {FilterSchema} from "../dataSources/DataSource";
import {TableDataSource} from "../dataSources/TableDataSource";
import {BasicDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";
import {GQLResponse} from "../gqlModel/types";
import {queryGqlApi} from "../../graphql/queryGqlApi";
import {listIssues} from "../../graphql/reliability/queries";
import {onCreateIssue, onUpdateIssue, onDeleteIssue} from "../../graphql/reliability/subscriptions";
import {InTextLabel} from "../../ui/components/InTextLabel/InTextLabel";
import {MuiIcon} from "../../ui/components/icons/MuiIcon";
import {IssueRowActions} from "../../ui/screens/Issues/IssueRowActions";

import {Issue, IssueSourceServer} from "./types";

const ENDPOINT = "pgServer";

const colOrder: ColId[] = ['issue', 'location', 'status', 'date', 'actions'];

const colSchema: ColSchema = {
    issue:    {getTitle: () => intl.get('issues.col.issue').d('Issue'),    alignLeft: true, minWidth: 300},
    location: {getTitle: () => intl.get('issues.col.location').d('Location'), alignLeft: true, minWidth: 140},
    status:   {getTitle: () => intl.get('issues.col.status').d('Status'),  alignLeft: true, minWidth: 110},
    date:     {getTitle: () => intl.get('issues.col.date').d('Date'),      alignLeft: true, minWidth: 120},
    actions:  {getTitle: () => '', minWidth: 56, maxWidth: 56},
};

const SOURCE_MAP: Record<IssueSourceServer, {icon: string; tint: 'danger' | 'info' | 'warning'}> = {
    audit_finding: {icon: 'fact_check',   tint: 'danger'},
    cant_complete: {icon: 'flag',          tint: 'danger'},
    manual_report: {icon: 'chat_bubble',   tint: 'info'},
    overdue:       {icon: 'schedule',      tint: 'warning'},
    missed:        {icon: 'disabled_by_default', tint: 'danger'},
};

const STATUS_META: Record<string, {type: 'info' | 'success' | 'neutral'; text: () => string}> = {
    open:      {type: 'info',    text: () => intl.get('issues.status.open').d('Open')},
    resolved:  {type: 'success', text: () => intl.get('issues.status.resolved').d('Resolved')},
    dismissed: {type: 'neutral', text: () => intl.get('issues.status.dismissed').d('Dismissed')},
};

/**
 * Live list of reliability issues for a workspace.
 * Pure query + subscription layer — no mutations here.
 * Mutations live in IssueGqlProvider; user-facing flows in src/actions/issue/.
 */
export class IssueDS extends TableDataSource<Issue> {
    readonly _modelName = 'Issue' as const;
    readonly wsId: string;

    readonly _subscriptionGQLs = {
        onCreate: onCreateIssue,
        onUpdate: onUpdateIssue,
        onDelete: onDeleteIssue,
    };

    /** Set from Issues.tsx before render — resolves locationId/taskListUnitId → display name. */
    resolveLocationName: (issue: Issue) => string = () => "—";
    /** Set from Issues.tsx — used by the 'mine' filter. */
    currentUserId: string = "";
    /** Set from Issues.tsx — resolves reportedBy userId → display name ("You" for the current user). */
    resolveUserName: (userId?: string | null) => string | undefined = () => undefined;

    constructor(deps: BasicDataSourceDeps, wsId: string) {
        super(deps);
        this.wsId = wsId;
        this.setSortRule('lastSignalAt');
        makeObservable(this, {
            openIssues: computed,
        });
    }

    get syncHash() { return this.wsId; }
    get syncQueryFilter() { return {workspaceId: this.wsId}; }

    async loadGQLFetch(): Promise<GQLResponse<Issue>> {
        return queryGqlApi(
            listIssues,
            {workspaceId: this.wsId, limit: 200},
            this.deps.dataClientService,
            this.deps.authService,
            ENDPOINT,
        ) as Promise<GQLResponse<Issue>>;
    }

    async loadMoreGQLFetch(nextToken: string): Promise<GQLResponse<Issue>> {
        return queryGqlApi(
            listIssues,
            {workspaceId: this.wsId, limit: 200, nextToken},
            this.deps.dataClientService,
            this.deps.authService,
            ENDPOINT,
        ) as Promise<GQLResponse<Issue>>;
    }

    getWsClient() {
        return this.deps.dataClientService.getWsClientByKey(ENDPOINT);
    }

    getColsSchema(): ColSchema { return colSchema; }
    getColOrder(): ColId[] { return colOrder; }

    getFilterSchema(): FilterSchema<Issue> {
        return {
            text: {
                type: 'client',
                apply: (issue) => {
                    const q = (this.filterValues.text ?? '').toLowerCase().trim();
                    if (!q) return true;
                    const hay = [issue.title, this.resolveLocationName(issue)].join(' ').toLowerCase();
                    return hay.includes(q);
                },
            },
            sourceTab: {
                type: 'client',
                apply: (issue) => {
                    const tab = this.filterValues.sourceTab ?? 'all';
                    if (!tab || tab === 'all') return true;
                    if (tab === 'overdue') return issue.source === 'overdue';
                    if (tab === 'missed') return issue.source === 'missed';
                    if (tab === 'flags') return issue.source === 'cant_complete' || issue.source === 'manual_report';
                    if (tab === 'audit') return issue.source === 'audit_finding';
                    return true;
                },
            },
            status: {
                type: 'client',
                apply: (issue) => {
                    const s = this.filterValues.status ?? 'open';
                    if (!s || s === 'all') return true;
                    if (s === 'archived') return !!issue.archivedAt;
                    return issue.status === s;
                },
            },
            location: {
                type: 'client',
                apply: (issue) => {
                    const loc = this.filterValues.location ?? 'all';
                    if (!loc || loc === 'all') return true;
                    return issue.locationId === loc || issue.taskListUnitId === loc;
                },
            },
            mine: {
                type: 'client',
                apply: (issue) => {
                    if (!this.filterValues.mine) return true;
                    return issue.reportedBy === this.currentUserId;
                },
            },
            // Reused by the shared UserFilter component (src/ui/screens/Quizzes/common/table-filters/UserFilter.tsx),
            // which always reads/writes filterValues.userId — repurposed here as "who reported the issue".
            userId: {
                type: 'client',
                apply: (issue) => {
                    const reporterId = this.filterValues.userId;
                    if (!reporterId) return true;
                    return issue.reportedBy === reporterId;
                },
            },
        };
    }

    getFilterDefaultValues() {
        return {text: '', sourceTab: 'all', status: 'open', location: 'all', mine: false, userId: null};
    }

    mapResultToTableRow = (issue: Issue) => {
        const src = SOURCE_MAP[issue.source] ?? SOURCE_MAP.manual_report;
        const statusMeta = STATUS_META[issue.status] ?? STATUS_META.open;
        const d = new Date(issue.lastSignalAt ?? issue.createdAt);
        const date = isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, {day: 'numeric', month: 'short'});

        return {
            id: issue.id,
            issue: (
                <span className="IssueDS-issueCell">
                    <span className={`Issues-srcIcon Issues-srcIcon--${src.tint}`}>
                        <MuiIcon code={src.icon as any} size={16}/>
                    </span>
                    <span className="IssueDS-issueText">{issue.title}</span>
                </span>
            ),
            location: this.resolveLocationName(issue),
            status: <InTextLabel type={statusMeta.type} size="sm" text={statusMeta.text()}/>,
            date,
            actions: <IssueRowActions issue={issue}/>,
            raw: issue,
        };
    };

    get openIssues(): Issue[] {
        return this.allRows.filter(i => i.status === 'open' && !i.archivedAt);
    }

    getById(id: string): Issue | undefined {
        return this.allRows.find(i => i.id === id);
    }

    /**
     * Manual optimistic update for callers that just ran a mutation. Subscriptions
     * keep this in sync on a live server, but the mock backend never fires them
     * (see resolversReliabilityAudit.ts), so action call sites must call this too.
     */
    upsertIssue(issue: Issue) {
        this._upsertItem(issue, true);
    }

    removeIssue(id: string) {
        this._removeItem(id);
    }
}
